// Restoring a stored sign-in at launch renews an expired ID token with the refresh token, a call to Cognito. Right
// after a phone wakes the network may not be up yet, and one failed call sent a signed-in user to the sign-in page
// (their tokens stayed stored, so the next launch restored them). A failure that can pass is retried for a few
// seconds; one that cannot (nothing stored, a refresh token expired or revoked, a deleted user) means no session.

// Under the mobile app's own timers: startup recovery at 20 s, the HCA "Restoring session" overlay at 60 s.
export const SESSION_RESTORE_RETRY_BUDGET_MS = 12000;
const RETRY_DELAYS_MS = [500, 1000, 2000, 3000];
const TRANSIENT_ERROR_CODES = [
	"NetworkError",
	"TimeoutError",
	"RequestTimeout",
	"RequestTimeoutException",
	"TooManyRequestsException",
	"ThrottlingException",
	"InternalErrorException",
	"ServiceUnavailable",
	"ServiceUnavailableException"
];

/**
 * Whether a failure to read the stored session can pass: the network is not up (amazon-cognito-identity-js reports
 * { code: "NetworkError", message: "Network error" }), a timeout, Cognito throttling or failing (5xx). Amplify's own
 * strings ("No current user") and Cognito's NotAuthorizedException ("Refresh Token has expired", "Refresh Token has
 * been revoked") or UserNotFoundException mean there is no session.
 */
export function isTransientSessionRestoreError(error) {
	if (!error || typeof error !== "object") {
		return false;
	}
	if (TRANSIENT_ERROR_CODES.includes(String(error.code || error.name || ""))) {
		return true;
	}
	if (Number(error.statusCode) >= 500) {
		return true;
	}
	return /^network error$|failed to fetch|timed? ?out/i.test(String(error.message || ""));
}

export function describeSessionRestoreError(error) {
	if (!error || typeof error !== "object") {
		return `${error || ""}`;
	}
	return [error.code || error.name, error.message].filter(Boolean).join(": ");
}

/**
 * The stored user and session, renewing expired tokens. Auth.currentAuthenticatedUser() turns every failure into
 * "The user is not authenticated", while Auth.currentSession() passes Cognito's error through, so the session is
 * read first and a failure says what went wrong.
 *
 * @param {Object} auth Amplify's Auth.
 */
export async function readStoredSession(auth) {
	const session = await auth.currentSession();
	const cognitoUser = await auth.currentAuthenticatedUser();
	return { cognitoUser, session };
}

const isBrowserOnline = () => typeof navigator === "undefined" || navigator.onLine !== false;

const waitForBrowserOnline = timeoutMs => new Promise(resolve => {
	if (typeof window === "undefined" || typeof window.addEventListener !== "function") {
		resolve();
		return;
	}
	let timer = null;
	const done = () => {
		clearTimeout(timer);
		window.removeEventListener("online", done);
		resolve();
	};
	timer = setTimeout(done, Math.max(0, timeoutMs));
	window.addEventListener("online", done);
});

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * readSession() until it succeeds. A failure that can pass is retried until budgetMs from the first attempt (an
 * attempt in flight is never cut short); offline, it waits for the browser to come back online instead. Throws the
 * failure when there is no session or the budget runs out.
 *
 * @param {Function} readSession Resolves to the session (readStoredSession).
 * @param {Function} [options.log] (event, details) for each failed attempt and a restore after a retry.
 */
export async function restoreSessionWithRetry(readSession, {
	budgetMs = SESSION_RESTORE_RETRY_BUDGET_MS,
	log = () => {},
	now = () => Date.now(),
	wait = delay,
	isOnline = isBrowserOnline,
	waitForOnline = waitForBrowserOnline
} = {}) {
	const startedAt = now();
	for (let attempt = 1; ; attempt++) {
		try {
			const result = await readSession();
			if (attempt > 1) {
				log("session restored after retry", { attempt, waitMs: now() - startedAt });
			}
			return result;
		} catch (error) {
			const waitMs = now() - startedAt;
			const retryInMs = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length) - 1];
			const transient = isTransientSessionRestoreError(error);
			const online = isOnline();
			const next = !transient || waitMs >= budgetMs || (online && waitMs + retryInMs >= budgetMs)
				? "no session"
				: (online ? "retry" : "wait for online");
			log("session check failed", {
				attempt,
				waitMs,
				error: describeSessionRestoreError(error),
				transient,
				online,
				next,
				retryInMs: next === "retry" ? retryInMs : 0
			});
			if (next === "no session") {
				throw error;
			}
			if (next === "retry") {
				await wait(retryInMs);
			} else {
				await waitForOnline(budgetMs - waitMs);
				if (!isOnline()) {
					log("session check still offline", { attempt, waitMs: now() - startedAt });
					throw error;
				}
			}
		}
	}
}
